import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Box } from "@mui/material";
import { Button, TextLinkCaret } from "@vds/buttons";
import { Input } from "@vds/inputs";
import { Loader } from "@vds/loaders";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Title } from "@vds/typography";
import UploadAppearance from "../components/UploadAppearance";
import ErrorModal from "../components/ErrorModal";
import DeployModal from "../components/DeployModal";
import BreadcrumbsContainer from "../../../global/components/BreadcrumbsContainer";
import { createAppearanceRequest, fetchAppearancesRequest, resetCreateStates, resetCreateSuccess, clearAppearanceError } from "../../../store/actions/ea-actions";
import { APPEARANCE_TEXT, CREATE_APPEARANCE, ARIA_LABELS, APPEARANCE_RUNTIME_STATUS } from "../../../constants/enrolledAppearanceConstants";
import "./styles.css";
import { CREATE_APPEARANCE_REGEX, ROUTES } from "../../../global/constants/routesConstants";
import useUserRole from "../../../global/hooks/useUserRole";

const EACreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const appearanceIdFromQuery = searchParams.get("id") || null;
  const { isSiteManager } = useUserRole();

  // Step constants for multi-step form
  const CREATE_NAME_STEP = 0;
  const UPLOAD_IMAGES_STEP = 1;

  const [index, setIndex] = useState(CREATE_NAME_STEP);
  const [mode, setMode] = useState("create");
  const [errorConfig, setErrorConfig] = useState({
    title: "",
    body: "",
    ariaLabel: "",
    onClose: () => {}
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(false);

  const [appearanceName, setAppearanceName] = useState("");
  const [isNameCharInvalid, setIsNameCharInvalid] = useState(false);
  const { createLoading, createError, createSuccess, createdAppearance, uploadCount, imageUploadStatus, imageDeleteStatus, appearances } = useSelector(
    (state) => state.enrolledAppearanceReducer
  );
  const { loading, error, editAppearance } = useSelector((state) => state.enrolledAppearanceReducer);
  const selectedSiteId = useSelector((state) => state.sitesReducer.selectedSiteId);
  const isEdit = mode === "edit";
  const fromAdvancedConfig = location.state?.fromAdvancedConfig;
  const returnPath = location.state?.returnPath;
  const polygonState = location.state?.polygonState;
  const breadcrumbItems = location.state?.breadcrumbItems;
  const savedPayload = location.state?.savedPayload;

  const INPUT_FIELD_MIN_LENGTH = 5;
  const INPUT_FIELD_MAX_LENGTH = 50;
  const breadcrumbConfig = fromAdvancedConfig && breadcrumbItems?.length
    ? [
        ...breadcrumbItems,
        { navigateTo: null, breadcrumbText: isEdit ? APPEARANCE_TEXT.EDIT_APPEARANCE : APPEARANCE_TEXT.CREATE_APPEARANCE }
      ]
    : [
        { navigateTo: ROUTES.SETTINGS, breadcrumbText: APPEARANCE_TEXT.BREADCRUMB_SETTINGS },
        { navigateTo: ROUTES.ENROLLED_APPEARANCE, breadcrumbText: APPEARANCE_TEXT.PAGE_TITLE },
        { navigateTo: null, breadcrumbText: isEdit ? APPEARANCE_TEXT.EDIT_APPEARANCE : APPEARANCE_TEXT.CREATE_APPEARANCE }
      ];

  const getNameInputErrorText = () => {
    if (isDuplicateName) return CREATE_APPEARANCE.DUPLICATE_APPEARANCE_NAME_ERROR;
    if (isNameCharInvalid) return CREATE_APPEARANCE.INPUT_VALIDATION_ERROR;
    if (isAppearanceNameInvalid()) return CREATE_APPEARANCE.MIN_LENGTH_ERROR;
    return CREATE_APPEARANCE.APPEARANCE_NAME_REQUIRED;
  };

  const handleNameChange = ({ target }) => {
    const name = target.value;
    setAppearanceName(name);

    if (name.length >= INPUT_FIELD_MIN_LENGTH) {
      setIsNameCharInvalid(!name.match(CREATE_APPEARANCE_REGEX));
    } else {
      setIsNameCharInvalid(false);
    }

    const appearanceNameMap = new Map(
      (appearances || []).map((appearance) => [appearance.name?.toLowerCase(), appearance.id])
    );
    const isDuplicate =
      name.length > 0 &&
      appearanceNameMap.has(name.toLowerCase()) &&
      appearanceNameMap.get(name.toLowerCase()) !== appearanceIdFromQuery;
    setIsDuplicateName(isDuplicate);
  };

  const handleCancel = () => {
    if (fromAdvancedConfig) {
      navigate(returnPath, { state: { ...polygonState, reopenAdvancedConfig: true, savedPayload } });
      return;
    }
    const enrolledSiteIds = isEdit ? (editAppearance?.enrolledSiteIds || []) : (createdAppearance?.enrolledSiteIds || []);
    const siteNotEnrolled = selectedSiteId && !enrolledSiteIds.includes(selectedSiteId);
    if (index === UPLOAD_IMAGES_STEP && isSiteManager && createdAppearance?.id && siteNotEnrolled) {
      setShowDeployModal(true);
    } else {
      navigate(ROUTES.ENROLLED_APPEARANCE);
    }
  };

  const hasAnyUploadSucceeded = Object.values(imageUploadStatus).some(({ status }) => status === "success");

  const isDoItLaterDisabled = () => {
    // In create flow, disable "Do it later" once any image upload succeeds
    if (uploadCount > 0) return true;
    return false;
  };

  const hasImagesChanged = () => {
    const hasDeleteOccurred = Object.values(imageDeleteStatus).some(({ status }) => status === "deleted");
    const hasUploadOccurred = Object.values(imageUploadStatus).some(({ status }) => status === "success");
    return hasDeleteOccurred || hasUploadOccurred;
  };

  const isDoneButtonDisabled = () => {
    // In create mode, disable if no successful uploads yet
    if (!isEdit && !uploadCount) return true;

    // In edit mode, disable Done until images are added or deleted
    if (isEdit && !hasImagesChanged()) return true;

    // Check if any upload is in progress
    const isUploadInProgress = Object.values(imageUploadStatus).some(({ status }) => status === "uploading");
    if (isUploadInProgress) return true;

    // Check if any delete is in progress
    const isDeleteInProgress = Object.values(imageDeleteStatus).some(({ status }) => status === "deleting");
    if (isDeleteInProgress) return true;

    return false;
  };

  const isCancelDisabled = () => {
    // In edit mode, disable Cancel only when Done is enabled
    if (isEdit && !isDoneButtonDisabled()) return true;
    return false;
  };

  const isAppearanceNameInvalid = () => {
    return appearanceName && appearanceName.length > 0 && appearanceName.length < INPUT_FIELD_MIN_LENGTH;
  };

  const handleNext = () => {
    dispatch(createAppearanceRequest(appearanceName.trim()));
  };

  const handleDone = () => {
    if (fromAdvancedConfig) {
      navigate(returnPath, { state: { ...polygonState, reopenAdvancedConfig: true, savedPayload } });
      return;
    }
    const enrolledSiteIds = isEdit ? (editAppearance?.enrolledSiteIds || []) : (createdAppearance?.enrolledSiteIds || []);
    const siteNotEnrolled = selectedSiteId && !enrolledSiteIds.includes(selectedSiteId);
    if (isSiteManager && siteNotEnrolled) {
      setShowDeployModal(true);
    } else {
      navigate(ROUTES.ENROLLED_APPEARANCE);
    }
  };

  const handleDeployModalClose = () => {
    setShowDeployModal(false);
    navigate(ROUTES.ENROLLED_APPEARANCE);
  };

  const getAppearanceId = (isEdit) => {
    if (isEdit) {
      return appearanceIdFromQuery;
    }

    return createdAppearance.id;
  };

  const getUploadedImages = (isEdit) => {
    if (!isEdit) return [];

    return editAppearance?.images || [];
  };

  const getLength = (obj) => {
    return Object.keys(obj || {})?.length;
  };

  useEffect(() => {
    if (!appearances || appearances.length === 0) {
      dispatch(fetchAppearancesRequest());
    }
  }, []);

  useEffect(() => {
    if (appearanceIdFromQuery) {
      setMode("edit");

      if (!getLength(editAppearance)) {
        dispatch(
          fetchAppearancesRequest({
            resourceidentifier: {
              id: appearanceIdFromQuery
            }
          })
        );
      }
    }
  }, [appearanceIdFromQuery]);

  useEffect(() => {
    if (createSuccess) {
      dispatch(resetCreateSuccess());
      setIndex((prev) => prev + 1);
    }
  }, [createSuccess]);

  useEffect(() => {
    if (getLength(editAppearance)) {
      setAppearanceName(editAppearance.name);
    }
  }, [getLength(editAppearance)]);

  const handleFetchErrorClose = () => {
    dispatch(clearAppearanceError());
    setShowErrorModal(false);
    navigate(ROUTES.ENROLLED_APPEARANCE);
  };

  useEffect(() => {
    if (error && !showErrorModal) {
      setErrorConfig({
        title: CREATE_APPEARANCE.FAILED_TO_GET_APPEARANCE,
        body: error.message || error,
        ariaLabel: ARIA_LABELS.LIST_FAILURE_ERROR,
        onClose: handleFetchErrorClose
      });
      setShowErrorModal(true);
    }
  }, [error]);

  // Clears stale state on mount; re-enables Next button after a create error so user can retry
  useEffect(() => {
    dispatch(resetCreateStates());
  }, [createError]);

  return (
    <div className="eacreate-page">
      {showErrorModal && (
        <ErrorModal
          opened={showErrorModal}
          title={errorConfig.title}
          body={errorConfig.body}
          footerButtonText={CREATE_APPEARANCE.CLOSE}
          onClose={errorConfig.onClose}
          ariaLabel={errorConfig.ariaLabel}
        />
      )}
      {showDeployModal && createdAppearance?.id && (
        <DeployModal
          appearanceName={createdAppearance.name || appearanceName}
          appearanceId={createdAppearance.id}
          tagids={createdAppearance.enrolledSiteIds || []}
          appearanceStatus={createdAppearance.status || APPEARANCE_RUNTIME_STATUS.NEW}
          deployments={createdAppearance.deployments || []}
          opened={showDeployModal}
          closeModal={handleDeployModalClose}
          onDeploySuccess={handleDeployModalClose}
          onDeployBlockAttempt={() => {}}
          onDeployFailure={() => {}}
          infoMessage={APPEARANCE_TEXT.MANAGE_APPEARANCE_ENROLL_MESSAGE}
        />
      )}
      {createLoading && <Loader active={createLoading} fullscreen={true} />}
      <Box>
        <BreadcrumbsContainer config={breadcrumbConfig} />
      </Box>
      <Container className="eacreate-title-container">
        <Box>
          <Title size="large" data-testid="eacreate-page-title">
            {isEdit ? APPEARANCE_TEXT.EDIT_APPEARANCE : APPEARANCE_TEXT.CREATE_APPEARANCE}
          </Title>
        </Box>
        <Button
          className="eacreate-cancel-button"
          size="large"
          use="secondary"
          disabled={index === UPLOAD_IMAGES_STEP ? isDoItLaterDisabled() : isCancelDisabled()}
          onClick={handleCancel}
        >
          {index === UPLOAD_IMAGES_STEP ? APPEARANCE_TEXT.DO_IT_LATER : APPEARANCE_TEXT.CANCEL_APPEARANCE}
        </Button>
      </Container>
      <Container className="eacreate-name-appearance-container">
        <Title className="eacreate-name-appearance" size="medium" data-testid="eacreate-name-appearance">
          {index === CREATE_NAME_STEP ? APPEARANCE_TEXT.NAME_APPEARANCE : APPEARANCE_TEXT.UPLOAD_IMAGES + appearanceName}
        </Title>
        <Box className="ea-create_appearance_box">
          {(index === CREATE_NAME_STEP || isEdit) && (
            <Input
              type="text"
              label="Appearance name"
              name="appearanceName"
              required={true}
              value={appearanceName}
              width="40%"
              error={isAppearanceNameInvalid() || isDuplicateName || isNameCharInvalid}
              errorText={getNameInputErrorText()}
              onChange={handleNameChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              minLength={INPUT_FIELD_MIN_LENGTH}
              maxLength={INPUT_FIELD_MAX_LENGTH}
              disabled={isEdit}
            />
          )}
          {(index === UPLOAD_IMAGES_STEP || isEdit) && (
            <UploadAppearance appearanceId={getAppearanceId(isEdit)} isEditMode={isEdit} images={getUploadedImages(isEdit)} />
          )}
        </Box>
        <Box className="eacreate-btn-container">
          {index === CREATE_NAME_STEP && !isEdit ? (
            <Button
              className="eacreate-next-button"
              size="large"
              use="primary"
              disabled={!appearanceName || isAppearanceNameInvalid() || isDuplicateName || isNameCharInvalid || createError || createLoading}
              onClick={handleNext}
            >
              {APPEARANCE_TEXT.NEXT_BUTTON}
            </Button>
          ) : (
            <Button disabled={isDoneButtonDisabled()} className="eacreate-done-button" size="large" use="primary" onClick={handleDone}>
              {APPEARANCE_TEXT.DONE_BUTTON}
            </Button>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default EACreate;
